
import React, { useRef, useState, useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { FiMoreVertical } from "react-icons/fi";
import { MenuItem, Button, Menu as MuiMenu } from "@mui/material";
import { Menu } from "@mui/material";
import {
  RiDeleteBinLine,
  RiDownloadFill,
  RiFilePdf2Line,
  RiMore2Fill,
  RiPrinterLine,
  RiArrowDownSLine,
} from "react-icons/ri";
import { usePermissions } from "@/hooks/usePermissions";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { TERipple } from "tw-elements-react";
import materialeData from "@/assets/data/materiale-didattico.json";
import QuestionsRecapModal from "./modals/QuestionsRecapModal";
import UploadMaterialModal from "../Corsi/CreateCourse/components/UploadMaterialModal";

interface Tab {
  id: string;
  label: string;
  count: number;
}

export default function MaterialeDidatticoPage() {
  const gridRef = useRef<AgGridReact>(null);
  const [showIcons, setShowIcons] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState<string>("SCORM");
  const [uploadButtonAnchorEl, setUploadButtonAnchorEl] = useState<null | HTMLElement>(null);
  const [questionsModalOpen, setQuestionsModalOpen] = useState(false);
  const [selectedMaterialTitle, setSelectedMaterialTitle] = useState("");
  const [activeUploadModal, setActiveUploadModal] = useState<'video' | 'scorm' | 'pdf' | null>(null);
  const { canInsert, canEdit, canDelete } = usePermissions();

  // Create tabs with counts for each material type
  const typeTabs = useMemo(() => {
    const materialTypes = ["SCORM", "Video", "PDF"];
    const tabsData: Tab[] = materialTypes.map((type) => ({
      id: type,
      label: type,
      count: materialeData.filter((mat: any) => mat.tipo === type).length
    }));
    return tabsData;
  }, []);

  // Filter data based on search term and selected type
  const filteredRowData = useMemo(() => {
    let filtered = materialeData;
    
    // Filter by material type
    filtered = filtered.filter((row: any) => row.tipo === selectedType);
    
    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter((row: any) => 
        Object.values(row).some((value) =>
          String(value)?.toLowerCase().includes(searchTerm?.toLowerCase())
        )
      );
    }
    
    return filtered;
  }, [selectedType, searchTerm]);

  // Function to handle type tab change
  const handleTypeTabChange = (typeId: string) => {
    setSelectedType(typeId);
  };

  // Function to handle upload button dropdown
  const handleUploadButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    setUploadButtonAnchorEl(event.currentTarget);
  };

  const handleUploadButtonClose = () => {
    setUploadButtonAnchorEl(null);
  };

  const handleUploadMaterial = (materialType: 'video' | 'scorm' | 'pdf') => {
    setActiveUploadModal(materialType);
    handleUploadButtonClose();
  };

  const closeUploadModal = () => {
    setActiveUploadModal(null);
  };

  const handleOpenQuestionsModal = (materialData: any) => {
    setSelectedMaterialTitle(`Domande del ${materialData.nomeFile}`);
    setQuestionsModalOpen(true);
  };

  function getParams() {
    return {
      skipColumnGroupHeaders: false,
      skipColumnHeaders: false,
      onlySelected: true,
      columnKeys: ["codice", "nomeFile", "tempoStudio", "associazioneCorso", "domAssociate", "contentId"],
    };
  }

  const deleteMultipleMateriali = async () => {
    const selectedRows = gridRef.current?.api.getSelectedRows();
    console.log(selectedRows);
    if (selectedRows !== undefined) {
      // Qui implementeresti la logica di eliminazione
    }
    gridRef.current?.api.applyTransaction({
      remove: selectedRows,
    });
  };

  function onBtnExport() {
    gridRef.current?.api!.exportDataAsCsv(getParams());
  }

  function ActionCellRenderer(params: ICellRendererParams) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const confirmDelete = () => {
      // Qui implementeresti la logica di eliminazione del materiale
      console.log("Eliminazione materiale:", params.data.id);
      setShowDeleteConfirm(false);
    };

    return (
      <div className="flex flex-row gap-2 justify-center items-center h-full">
        {canEdit("sezione_corsi") && (
          <i
            className="ri-pencil-line text-[23px] text-gray-600 cursor-pointer hover:text-red-700 transition-all duration-500"
            onClick={() => {
              console.log("Modifica materiale:", params.data.id);
            }}
          />
        )}
        {canDelete("sezione_corsi") && (
          <>
            <span onClick={handleClick}>
              <FiMoreVertical className="text-[23px] text-gray-600 cursor-pointer hover:text-red-700 transition-all duration-500" />
            </span>
            <Menu
              id="basic-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              MenuListProps={{
                "aria-labelledby": "basic-button",
              }}
            >
              <MenuItem
                onClick={() => {
                  handleClose();
                  console.log("Modifica dettagli materiale:", params.data.id);
                }}
              >
                Modifica dettagli
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleClose();
                  handleOpenQuestionsModal(params.data);
                }}
              >
                Gestione domande test
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleClose();
                  setShowDeleteConfirm(true);
                }}
              >
                Elimina
              </MenuItem>
            </Menu>
            {/* Delete Confirmation Modal */}
            <Dialog
              open={showDeleteConfirm}
              onClose={() => setShowDeleteConfirm(false)}
            >
              <DialogTitle style={{ fontWeight: "700" }}>
                Conferma eliminazione
              </DialogTitle>
              <DialogContent>
                <DialogContentText>
                  Sei sicuro di voler eliminare questo materiale? Questa azione non
                  può essere annullata.
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <TERipple rippleColor="light" className="mr-2 mb-3">
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(false)}
                    className="px-6 pb-2.5 pt-3 bg-primary-100 rounded text-sm font-medium border-none ring-0 outline-none uppercase leading-normal text-primary-800 transition duration-150 ease-in-out hover:bg-gray-300 hover:border-none hover:outline-none hover:ring-0 focus:outline-none focus:ring-0"
                  >
                    ANNULLA
                  </button>
                </TERipple>
                <TERipple rippleColor="light" className="mr-3 mb-3">
                  <button
                    type="button"
                    onClick={confirmDelete}
                    className="rounded bg-red-700 px-6 pb-2.5 pt-3 text-sm border-none ring-0 outline-none font-medium uppercase leading-normal text-white transition duration-150 ease-in-out hover:bg-red-700 hover:border-none hover:outline-none hover:ring-0 focus:outline-none focus:ring-0"
                  >
                    CONFERMA
                  </button>
                </TERipple>
              </DialogActions>
            </Dialog>
          </>
        )}
      </div>
    );
  }

  const handleSelectionChanged = () => {
    const selectedRows = gridRef.current?.api.getSelectedRows();
    const anyRowSelected = selectedRows && selectedRows.length > 0;
    setShowIcons(anyRowSelected ?? false);
  };

  // Colonne specifiche per i materiali didattici
  const columnDefs: ColDef[] = [
    {
      headerName: "",
      field: "selection",
      checkboxSelection: true,
      headerCheckboxSelection: true,
      filter: false,
      width: 60,
      maxWidth: 60,
      resizable: false,
      cellClass: "ag-cell-center",
      headerClass: "ag-header-center",
    },
    {
      headerName: "CODICE",
      field: "codice",
      flex: 1,
      cellClass: "ag-cell-center",
      headerClass: "ag-header-center",
    },
    {
      headerName: "NOME FILE",
      field: "nomeFile",
      flex: 2,
      width: 280,
      minWidth: 280,
      cellClass: "ag-cell-center",
      headerClass: "ag-header-center",
      cellRenderer: (params: ICellRendererParams) => {
        return (
          <div 
            className=" hover:underline h-full flex items-center justify-center"
            onClick={() => {
              console.log("Visualizza materiale:", params.data.id);
            }}
          >
            {params.value}
          </div>
        );
      }
    },
    {
      headerName: "TEMPO DI STUDIO",
      field: "tempoStudio",
      flex: 1.2,
      cellClass: "ag-cell-center",
      headerClass: "ag-header-center",
    },
    {
      headerName: "ASSOCIAZIONE AL CORSO",
      field: "associazioneCorso",
      flex: 1.5,
      cellClass: "ag-cell-center",
      headerClass: "ag-header-center",
    },
    {
      headerName: "DOM. ASSOCIATE",
      field: "domAssociate",
      flex: 1.2,
      cellClass: "ag-cell-center",
      headerClass: "ag-header-center",
      cellRenderer: (params: ICellRendererParams) => {
        return (
          <div 
            className="font-bold text-[#B80F15] cursor-pointer hover:underline h-full flex items-center justify-center"
            onClick={() => {
              handleOpenQuestionsModal(params.data);
            }}
          >
            {params.value}
          </div>
        );
      }
    },
    {
      headerName: "CONTENT ID",
      field: "contentId",
      flex: 1.2,
      cellClass: "ag-cell-center",
      headerClass: "ag-header-center",
    },
    {
      headerName: "",
      field: "actions",
      cellRenderer: ActionCellRenderer,
      minWidth: 100,
      maxWidth: 100,
      cellClass: "ag-cell-center",
      headerClass: "ag-header-center",
      suppressMenu: true,
      sortable: false,
      filter: false,
      pinned: "right",
      resizable: false,
    },
  ];

  const LoadingOrNoDataOverlay = () => {
    if (!materialeData?.length) {
      return (
        <div className="flex justify-center items-center h-full text-gray-500">
          Nessuna riga trovata
        </div>
      );
    }
    return null;
  };

  return (
    <div className="flex flex-col h-full gap-0 ">
      {/* Filters Section */}
      <div className="bg-white border-b border-gray-200 ">
        <div className="px-0 pt-5 pb-0 bg-[#F3F3F3] ">
          <div className="flex justify-between items-center px-8 box-border ">
            {/* Title and counter */}
            <div className="flex gap-2 items-center">
              <h1 className="text-[24px] font-semibold text-gray-900">
                Materiale Didattico
              </h1>
              <span className="ml-2 mt-1 bg-red-200 text-[#B80F15] py-1 px-2.5 rounded-full text-sm font-bold">
                {filteredRowData?.length}
              </span>
              <div
                className={`flex flex-row gap-3 text-black ml-4 mt-1 text-lg ${
                  showIcons ? "block" : "hidden"
                }  transition-all duration-500`}
              >
                <RiDownloadFill
                  onClick={() => onBtnExport()}
                  className={`transition-all duration-200 ease-in-out cursor-pointer hover:text-shblue-500`}
                />
                <RiFilePdf2Line
                  className={`transition-all duration-200 ease-in-out cursor-pointer hover:text-shblue-500`}
                />
                <RiPrinterLine
                  onClick={() => {}}
                  className={`transition-all duration-200 ease-in-out cursor-pointer hover:text-shblue-500`}
                />
                <RiDeleteBinLine
                  onClick={deleteMultipleMateriali}
                  className={`transition-all duration-200 ease-in-out cursor-pointer hover:text-shblue-500`}
                />
                <RiMore2Fill
                  className={`transition-all duration-200 ease-in-out cursor-pointer hover:text-shblue-500`}
                />
              </div>
            </div>

            {/* Search and New button */}
            <div className="flex gap-3 items-center">
              {(canInsert("sezione_corsi") || true) && (
                <>
                  <Button
                    variant="contained"
                    onClick={handleUploadButtonClick}
                    endIcon={<RiArrowDownSLine className="text-[12px]"/>}
                    sx={{
                      backgroundColor: '#B80F15',
                      color: 'white',
                      borderRadius: '4px',
                      textTransform: 'none',
                      fontSize: '14px',
                      fontWeight: 500,
                      padding: '8px 20px',
                      '&:hover': {
                        backgroundColor: '#A00D12',
                      },
                      '&:focus': {
                        outline: 'none',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    Carica Materiale
                  </Button>
                  <MuiMenu
                    anchorEl={uploadButtonAnchorEl}
                    open={Boolean(uploadButtonAnchorEl)}
                    onClose={handleUploadButtonClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                    PaperProps={{
                      sx: {
                        mt: 1,
                        minWidth: 180,
                        boxShadow: '0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)',
                        borderRadius: '8px',
                      },
                    }}
                  >
                    <MenuItem onClick={() => handleUploadMaterial('scorm')}>
                      SCORM
                    </MenuItem>
                    <MenuItem onClick={() => handleUploadMaterial('video')}>
                      Video
                    </MenuItem>
                    <MenuItem onClick={() => handleUploadMaterial('pdf')}>
                      PDF
                    </MenuItem>
                  </MuiMenu>
                </>
              )}
              <div className="relative">
                <input
                  type="text"
                  placeholder="Ricerca"
                  className="pl-4 pr-10 py-[9px] border text-sm border-gray-300 rounded-[4px] focus:outline-none focus:border-black focus:ring-black focus:ring-[0.5px] text-gray-500 placeholder-gray-600"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <svg
                    className="w-4 h-4 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </span>
              </div>
            </div>
          </div>

          {/* Type Filter buttons */}
          <div className="flex items-center pt-3 pb-2 mt-2 space-x-4 px-8 overflow-x-auto border-b border-gray-200">
            {typeTabs.map((tab: Tab, index: number) => (
              <React.Fragment key={tab.id}>
                <span
                  className={`${
                    selectedType === tab.id
                      ? "text-[#B80F15]"
                      : "text-gray-500 hover:text-gray-700"
                  } text-[20px] font-medium cursor-pointer whitespace-nowrap`}
                  onClick={() => handleTypeTabChange(tab.id)}
                >
                  {tab.label} ({tab.count})
                </span>
                {index < typeTabs.length - 1 && (
                  <div className="h-6 w-[1px] bg-gray-500"></div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
      {/* Table */}
      <div className="overflow-y-hidden flex-1 overflow-x-clip ">
        <div className="w-full h-full rounded-none ag-theme-quartz">
          <AgGridReact
            ref={gridRef}
            domLayout="normal"
            autoSizeStrategy={{
              type: "fitGridWidth",
              defaultMinWidth: 100,
            }}
            rowSelection="multiple"
            suppressRowDeselection={true}
            suppressRowClickSelection={true}
            pagination={false}
            onSelectionChanged={handleSelectionChanged}
            paginationAutoPageSize={false}
            defaultColDef={{
              sortable: true,
              filter: true,
              resizable: true,
            }}
            pivotPanelShow={"always"}
            rowHeight={65}
            rowData={filteredRowData}
            getRowId={(params) => params.data.id}
            columnDefs={columnDefs}
            readOnlyEdit={true}
            rowGroupPanelShow={"always"}
            overlayNoRowsTemplate=""
            overlayLoadingTemplate=""
            noRowsOverlayComponent={LoadingOrNoDataOverlay}
            loadingOverlayComponent={LoadingOrNoDataOverlay}
          />
        </div>
      </div>

      {/* Questions Recap Modal */}
      <QuestionsRecapModal
        open={questionsModalOpen}
        onClose={() => setQuestionsModalOpen(false)}
        title={selectedMaterialTitle}
      />

      {/* Upload Material Modals */}
      <UploadMaterialModal 
        isOpen={activeUploadModal === 'video'} 
        onClose={closeUploadModal}
        materialType="video"
      />
      
      <UploadMaterialModal 
        isOpen={activeUploadModal === 'scorm'} 
        onClose={closeUploadModal}
        materialType="scorm"
      />
      
      <UploadMaterialModal 
        isOpen={activeUploadModal === 'pdf'} 
        onClose={closeUploadModal}
        materialType="pdf"
      />
    </div>
  );
}

